/**
 * Paige Scan's local agent as the browser reaches it: `https://127.0.0.1:{port}/paige-scan/v1`,
 * on the first of the agent's candidate ports that answers (`apps/scan/Paige.Scan/Agent/AgentPorts.cs`
 * holds the same list, and the web app's content security policy names each one). The agent never
 * uploads; the page takes the pages it hands back and stages them through its own session.
 */
export const SCAN_AGENT_PORTS = [54978, 54977, 54976] as const;

const PORT_KEY = 'paige:scan-agent:port';
const REACHED_KEY = 'paige:scan-agent:reached';

export type ScanAgentUnavailableReason =
  'not_installed' | 'agent_off' | 'blocked_by_browser' | 'wrong_site' | 'no_scanners';

export interface ScanAgentDevice {
  id: string;
  name: string;
  kind: 'wia' | 'twain';
}

export interface ScanAgentCapabilities {
  feeder: boolean;
  flatbed: boolean;
  duplex: boolean;
  resolutions: number[];
  /** The scanner can scan through its own window (a TWAIN driver); Paige Scan 1.5.14 and later. */
  driverWindow?: boolean;
  /** "Scanner settings…" is offered: its settings window opens alone and Paige keeps its setup. */
  driverSettings?: boolean;
  /** This computer remembers the scanner's own window as this scanner's choice. */
  useDriverWindow?: boolean;
  /** A setup from the scanner's settings window is saved and put back before each scan. */
  savedSettings?: boolean;
}

/** How "Scanner settings…" stands while the scanner's settings window is open, and after. */
export type ScannerSettingsPoll = {
  status: 'waiting' | 'saved' | 'cancelled' | 'failed';
  outcome?: ScanOutcomeCode;
};

export interface ScanRequest {
  device: string;
  dpi: number;
  color: 'color' | 'grayscale' | 'blackAndWhite';
  source: 'feeder' | 'flatbed';
  duplex: boolean;
  /** Leave blank sides out (the backs of one-sided pages in a two-sided scan); Paige Scan 1.5.2 and later. */
  skipBlank: boolean;
  /** The scanner's own window drives the scan (TWAIN; 1.5.14), remembered for the scanner by Paige Scan. */
  driverWindow?: boolean;
  /** The job the pages are for (1.5.22): kept with them on disk, so the next sheet for this job offers them. */
  jobId?: string;
}

/**
 * A scan whose pages Paige Scan (1.5.22 and later) still keeps on disk because no page released them: the tab that
 * scanned them closed, reloaded or crashed before they were added to the job.
 */
export interface KeptScan {
  scan: string;
  pages: number;
  /** When the scan started (ISO 8601). */
  scannedAt: string;
  /** The job the scan was for, when its page said. */
  jobId?: string | null;
}

/**
 * How a scan ended, as Paige Scan 1.5.2 and later name it (`apps/scan/Paige.Scan.Twain/Session/ScanJob.cs`,
 * ScanOutcome, camel-cased). An older agent sends none; the page then reads its `status` and `reason`.
 */
export type ScanOutcomeCode =
  | 'completed'
  | 'cancelled'
  | 'noPaper'
  | 'paperJam'
  | 'doubleFeed'
  | 'coverOpen'
  | 'offline'
  | 'busy'
  | 'notFound'
  | 'noManager'
  | 'stoppedByScanner'
  | 'timedOut'
  | 'failed'
  | 'driverCrashed'
  | 'driverHung';

/** What an ending carries besides its status: the pages kept, blank sides left out, and for a failure the driver's detail. */
export interface ScanEnding {
  pages?: number;
  outcome?: ScanOutcomeCode;
  blank?: number;
  /** The driver's own words and codes, for the error report; never shown as the message. */
  detail?: string;
  /** wia, twain64 or twain32. */
  layer?: string;
}

export interface ScannedPage {
  index: number;
  /** Base64 JPEG: the page, or for a black-and-white page its preview. */
  jpeg: string;
  /** Base64 CCITT G4 TIFF of a black-and-white page (Paige Scan 1.5.2, when asked): what is uploaded. */
  tiff?: string | null;
  width: number;
  height: number;
}

export type ScanPoll =
  | { status: 'page'; page: ScannedPage }
  | { status: 'waiting' }
  | ({ status: 'done'; pages: number } & ScanEnding)
  | ({ status: 'cancelled' } & ScanEnding)
  | ({ status: 'failed'; reason: string } & ScanEnding);

export interface ScanAgent {
  port: number;
  version: string;
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/** Where the person's browser may or may not let a public page reach software on this computer. */
export type LocalNetworkPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

export function agentBase(port: number): string {
  return `https://127.0.0.1:${port}/paige-scan/v1`;
}

export function rememberedPort(): number | null {
  try {
    const stored = Number(window.localStorage.getItem(PORT_KEY));
    return (SCAN_AGENT_PORTS as readonly number[]).includes(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Whether this browser has ever reached the agent here: tells "agent off" from "not installed". */
export function everReached(): boolean {
  try {
    return window.localStorage.getItem(REACHED_KEY) === 'true';
  } catch {
    return false;
  }
}

function remember(port: number): void {
  try {
    window.localStorage.setItem(PORT_KEY, String(port));
    window.localStorage.setItem(REACHED_KEY, 'true');
  } catch {
    // Without storage the next visit sweeps the ports again.
  }
}

/** The ports in the order to try: the one that answered last time first. */
export function portsToTry(remembered: number | null = rememberedPort()): number[] {
  const rest = SCAN_AGENT_PORTS.filter((p) => p !== remembered);
  return remembered === null ? rest : [remembered, ...rest];
}

/** What the browser itself says about reaching software on this machine; most browsers say nothing. */
export async function localNetworkPermission(): Promise<LocalNetworkPermission> {
  try {
    const permissions = navigator.permissions;
    if (!permissions?.query) return 'unknown';
    const status = await permissions.query({
      name: 'local-network-access',
    } as unknown as PermissionDescriptor);
    return status.state;
  } catch {
    return 'unknown';
  }
}

async function withTimeout<T>(
  run: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await run(controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

async function askAgent(port: number, fetchFn: FetchLike, timeoutMs: number): Promise<ScanAgent> {
  const response = await withTimeout(
    (signal) => fetchFn(`${agentBase(port)}/agent`, { mode: 'cors', cache: 'no-store', signal }),
    timeoutMs,
  );
  if (!response.ok) throw new Error(`agent answered ${response.status}`);
  const body = (await response.json()) as { product?: string; version?: string };
  if (body.product !== 'Paige Scan') throw new Error('not Paige Scan');
  return { port, version: body.version ?? '' };
}

/**
 * Something answered on the port, whatever it said: a no-cors fetch resolves opaque on any TLS
 * connection and rejects when nothing listens (or the browser refuses the connection outright).
 */
async function anythingListening(port: number, fetchFn: FetchLike): Promise<boolean> {
  try {
    await withTimeout(
      (signal) =>
        fetchFn(`${agentBase(port)}/agent`, { mode: 'no-cors', cache: 'no-store', signal }),
      4000,
    );
    return true;
  } catch {
    return false;
  }
}

export interface FindAgentOptions {
  fetchFn?: FetchLike;
  permission?: () => Promise<LocalNetworkPermission>;
  reached?: boolean;
  remembered?: number | null;
}

export type FindAgentResult =
  | { agent: ScanAgent; reason?: undefined }
  | { agent?: undefined; reason: ScanAgentUnavailableReason };

/**
 * Every port at once, first Paige Scan wins. When nothing answers, the reason is worked out rather
 * than guessed: the browser's own permission state first, then whether anything at all listens
 * (an agent that refused this site), then whether this browser ever reached the agent here.
 * The first contact from a site may sit behind the browser's permission prompt, so it waits
 * longer than a repeat visit does.
 */
export async function findAgent(options: FindAgentOptions = {}): Promise<FindAgentResult> {
  const fetchFn = options.fetchFn ?? ((input, init) => fetch(input, init));
  const reached = options.reached ?? everReached();
  const ports = portsToTry(
    options.remembered === undefined ? rememberedPort() : options.remembered,
  );
  try {
    const agent = await Promise.any(
      ports.map((port) => askAgent(port, fetchFn, reached ? 4000 : 30000)),
    );
    remember(agent.port);
    return { agent };
  } catch {
    const permission = options.permission ?? localNetworkPermission;
    return { reason: await whyUnavailable(ports, fetchFn, permission, reached) };
  }
}

/** Nothing answered as Paige Scan: which of the four causes it is. */
async function whyUnavailable(
  ports: number[],
  fetchFn: FetchLike,
  permission: () => Promise<LocalNetworkPermission>,
  reached: boolean,
): Promise<ScanAgentUnavailableReason> {
  if ((await permission()) === 'denied') return 'blocked_by_browser';
  const listening = await Promise.all(ports.map((port) => anythingListening(port, fetchFn)));
  if (listening.some(Boolean)) return 'wrong_site';
  return reached ? 'agent_off' : 'not_installed';
}

/** The calls the scan sheet makes once the agent is found; each rejects with the agent's reason. */
export function scanAgentApi(agent: ScanAgent, fetchFn: FetchLike = (i, o) => fetch(i, o)) {
  const base = agentBase(agent.port);
  const call = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const response = await fetchFn(`${base}${path}`, { mode: 'cors', cache: 'no-store', ...init });
    // 202 is the window brought forward (`folders/open`): accepted, nothing to read.
    if (response.status === 204 || response.status === 202) return undefined as T;
    const body = (await response.json()) as T & { reason?: string };
    if (!response.ok) throw new Error(body.reason ?? `Paige Scan answered ${response.status}`);
    return body;
  };
  return {
    devices: async () => (await call<{ devices: ScanAgentDevice[] }>('/devices')).devices,
    capabilities: (device: string) =>
      call<ScanAgentCapabilities>(`/capabilities?device=${encodeURIComponent(device)}`),
    /** `acceptTiff`: this page uploads a black-and-white page as the G4 TIFF Paige Scan 1.5.2 sends. */
    begin: async (request: ScanRequest & { acceptTiff?: boolean }) =>
      (await call<{ scan: string }>('/scans', post(request))).scan,
    next: (scan: string) => call<ScanPoll>(`/scans/${scan}/next`),
    ...scannerWindowCalls(call),
    ...keptScanCalls(call),
    cancel: (scan: string) => call<void>(`/scans/${scan}/cancel`, { method: 'POST' }),
    /**
     * The code of the device request Paige Scan's folder monitor is waiting on, or null: the Allow
     * page skips typing the code only when this matches the link's (folder-monitor.md, section 3).
     */
    deviceRequest: async () =>
      (await call<{ userCode: string | null }>('/device-request')).userCode ?? null,
    /** Brings Paige Scan's Watched folders window forward with this job picked; the agent answers 202. */
    openFolders: (job: { jobId: string; jobName: string }) =>
      call<void>('/folders/open', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(job),
      }),
  };
}

export type ScanAgentApi = ReturnType<typeof scanAgentApi>;

type AgentCall = <T>(path: string, init?: RequestInit) => Promise<T>;

/** The scanner's own window (Paige Scan 1.5.14): its settings window alone, its reset, and the per-scanner choice. */
function scannerWindowCalls(call: AgentCall) {
  return {
    /** "Scanner settings…": the scanner's settings window alone; poll `settingsStatus` until it closes. */
    openSettings: async (device: string) =>
      (await call<{ settings: string }>('/scanner-settings', post({ device }))).settings,
    settingsStatus: (id: string) => call<ScannerSettingsPoll>(`/scanner-settings/${id}`),
    /** Forgets the setup the scanner's settings window left. */
    resetSettings: (device: string) => call<void>('/scanner-settings/reset', post({ device })),
    /** Remembers, for this scanner on this computer, whether its own window drives the scan. */
    rememberWindow: (device: string, driverWindow: boolean) =>
      call<void>('/scanner-settings/window', post({ device, driverWindow })),
  };
}

/**
 * Each scan's pages on Paige Scan's disk (1.5.22): released once they are in the job or discarded, else offered to the
 * next sheet. An older Paige Scan answers 404 to each; the caller treats that as "nothing kept".
 */
function keptScanCalls(call: AgentCall) {
  return {
    /** The scan's pages are in the job, or the person discarded them: Paige Scan deletes its copy. */
    release: (scan: string) => call<void>(`/scans/${scan}/release`, { method: 'POST' }),
    keptScans: async () => (await call<{ scans: KeptScan[] }>('/kept-scans')).scans,
    /** One kept page (1-based), in the shape a poll hands one over. */
    keptPage: (scan: string, index: number) =>
      call<ScannedPage>(`/kept-scans/${scan}/pages/${index}`),
    discardKept: (scan: string) => call<void>(`/kept-scans/${scan}/discard`, { method: 'POST' }),
  };
}

function post(body: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  };
}
